import { useLanguage, useTranslate } from 'context/languageContext';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useLazyGetSurveyByIdQuery } from 'redux/services/surveyService';
import { updateShowLoading } from 'redux/slices/contextualSlice';
import { surveysState } from 'redux/slices/surveySlice';
import { ISurveyQuestion, ISurveySimple } from 'types/interfaces';
import { object, string, number, array } from 'yup';

export default function useSurvey() {
  const dispatch = useDispatch();
  const allSurveys = useSelector(surveysState).allSurveys;
  const [getSurveyQuestions] = useLazyGetSurveyByIdQuery();
  const currentLanguage = useLanguage();
  const t = useTranslate();

  function handleOpinionResource(
    ambitoId: number,
    resourceId: number,
    handleAction: (survey: any) => void,
  ) {
    let foundSurveySpecific: ISurveySimple | undefined = undefined;
    if (ambitoId === 2) {
      //caso parada
      foundSurveySpecific = allSurveys.find(
        (survey) => survey.stopId === resourceId && survey.isAllowedToAnswer,
      );
    } else if (ambitoId === 3) {
      //caso línea
      foundSurveySpecific = allSurveys.find(
        (survey) => survey.lineId === resourceId && survey.isAllowedToAnswer,
      );
    } else if (ambitoId === 5) {
      //caso faq
      foundSurveySpecific = allSurveys.find(
        (survey) => survey.faqId === resourceId && survey.isAllowedToAnswer,
      );
    } else if (ambitoId === 6) {
      //caso poi
      foundSurveySpecific = allSurveys.find(
        (survey) => survey.poiId === resourceId && survey.isAllowedToAnswer,
      );
    }

    let idSurvey = 0;
    if (foundSurveySpecific) {
      idSurvey = foundSurveySpecific.id;
    } else {
      //se busca la encuesta genérica
      const foundSurveyGeneric = allSurveys.find(
        (survey) => survey.ambitoId === ambitoId && survey.isAllowedToAnswer && !survey.poiId && !survey.stopId && !survey.lineId && !survey.faqId,
      );
      if (foundSurveyGeneric) idSurvey = foundSurveyGeneric.id;
    }

    if (idSurvey) {
      dispatch(updateShowLoading(true));
      getSurveyQuestions({ id: idSurvey, locale: currentLanguage }).then((result) => {
        if (result?.isSuccess) {
          handleAction(result.data);
        }
        dispatch(updateShowLoading(false));
      });
    }
  }

  function obtainObjectSchema(questions: Array<ISurveyQuestion>) {
      let schema: any = {};

      for (const question of questions) {
        switch (question.typeId) {
          //selección única
          case 1:
            schema[`${question.label}-${question.id}`] = number().required(
              t('lb_option_unica_obligatoria'),
            );
            break;
          //selección múltiple
          case 2:
            schema[`${question.label}-${question.id}`] = array()
              .of(number())
              .required(t('lb_option_multiple_obligatoria'));
            break;
          //valoración (rango numérico)
          case 3:
            //schema[`${question.label}-${question.id}`] =  array().of(object({id: number(), value: string()})).required(t('lb_nombre_obligatorio'));
            for (const answerText of question.answers) {
              if (answerText.minScaleValue && answerText.maxScaleValue) {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: number().min(answerText.minScaleValue).max(answerText.maxScaleValue),
                })
                  .required(t('lb_numeric_obligatorio'))
                  .nonNullable(t('lb_numeric_obligatorio'))
                  .test(
                    'range',
                    t('lb_numeric_range'),
                    ({ id, value }) =>
                      (value ?? 0) >= (answerText.minScaleValue ?? 0) &&
                      (value ?? 0) <= (answerText.maxScaleValue ?? 0),
                  );
              } else if (answerText.minScaleValue) {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: number().min(answerText.minScaleValue),
                })
                  .required(t('lb_numeric_obligatorio'))
                  .nonNullable(t('lb_numeric_obligatorio'));
              } else if (answerText.maxScaleValue) {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: number().max(answerText.maxScaleValue),
                })
                  .required(t('lb_numeric_obligatorio'))
                  .nonNullable(t('lb_numeric_obligatorio'));
              } else {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: number(),
                })
                  .required(t('lb_numeric_obligatorio'))
                  .nonNullable(t('lb_numeric_obligatorio'));
              }
            }
            break;
          //texto corto
          case 4:
            for (const answerText of question.answers) {
              if (answerText.maxTextLength) {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: string(),
                })
                  .required(t('lb_text_obligatorio'))
                  .test(
                    'max-length',
                    `${t('lb_text_longitud_obligatorio')} ${answerText.maxTextLength + 1} ${t(
                      'lb_caracteres',
                    )}.`,
                    ({ id, value }) =>
                      (value?.length ?? 0) > 1 &&
                      (value?.length ?? 0) <= (answerText.maxTextLength ?? 0),
                  );
              } else {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: string().min(1, t('lb_text_obligatorio')),
                }).required(t('lb_text_obligatorio'));
              }
            }
            break;
          //texto largo
          case 5:
            for (const answerText of question.answers) {
              if (answerText.maxTextLength) {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: string(),
                })
                  .required(t('lb_text_obligatorio'))
                  .test(
                    'max-length',
                    `${t('lb_text_longitud_obligatorio')} ${answerText.maxTextLength + 1} ${t(
                      'lb_caracteres',
                    )}.`,
                    ({ id, value }) =>
                      (value?.length ?? 0) > 1 &&
                      (value?.length ?? 0) <= (answerText.maxTextLength ?? 0),
                  );
              } else {
                schema[`${answerText.label}-${answerText.id}`] = object({
                  id: number(),
                  value: string().min(1, t('lb_text_obligatorio')),
                }).required(t('lb_text_obligatorio'));
              }
            }
            break;
          default:
            break;
        }
      }

      return schema;
    }

  return { handleOpinionResource, obtainObjectSchema };
}
